// src/pages/Folder/Correlation.tsx

import {
  ArrowBackIosTwoTone,
  ArrowForwardIosTwoTone,
  NotificationImportantTwoTone,
  InfoTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
  Popover,
  Divider,
} from '@mui/material'
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as Recharts_Tooltip,
  Legend,
  Brush,
  ReferenceLine,
} from 'recharts'

import {
  FolderContractView,
  FolderId,
  FolderSingle,
} from '@/types/domain/folder'
import { SensorRecord, SensorPool, SensorId } from '@/types/domain/sensor'

import {
  colorSchemeConsumption,
  colorSchemeFeeling,
  colorSchemeSecurity,
  CORRECTION_ABSOLUT,
  CORRECTION_COOLING,
  CORRECTION_HEATING,
  correctionTypeData,
  daysAgo,
  daysOnward,
  FILTER_SUB_SENSOR,
  FILTER_TARGET_NOT_PERFORMANCE,
  FILTER_TARGET_NOT_SECURITY,
  FILTER_TARGET_SECURITY,
  getSession,
  normalizedSensorType as nst,
  setSession,
  SORT_NAME,
  SVGStatus,
  userLocales,
  WDDDMMYY,
  WDDDMMYYYYHHMMSS,
} from '../../api'
import {
  AccConsumption,
  AccFeeling,
  AccSecurity,
  HStack,
} from '../../components'
import {
  FolderStatus,
  useAppConfig,
  useConsumptionApi,
  useFolder,
  useFolderContract,
  useFolderSensors,
  useFolderStatus,
  useSecuritySensors,
  useSensorPool,
  useSensorPoolIds,
  useTimeSeriesApi,
} from '../../hooks'

// if given date is in future, return current date
const limitTodayMax = date =>
  date > new Date(Date.now()).toISOString().split('T')[0]
    ? new Date(Date.now()).toISOString().split('T')[0]
    : date

const removeBaselineSensor = (array, entity_id, type, value) => {
  let index

  type === 'alarm_min'
    ? (index = array.findIndex(
        s => s.entity_id === entity_id && s.alarm_min === value
      ))
    : (index = array.findIndex(
        s => s.entity_id === entity_id && s.alarm_max === value
      ))
  return index >= 0
    ? [...array.slice(0, index), ...array.slice(index + 1)]
    : array
}

const NOT_SPECIFIED = 'Nicht hinterlegt'

type CustomTooltipProps = {
  active?: boolean
  label?: string | number
  payload?: Array<any>
  userLocales?: string | string[]
  unitLookup?: Record<string, string>
}

const CustomTooltip = ({
  active,
  label,
  payload,
  userLocales,
  unitLookup,
}: CustomTooltipProps) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null

  return (
    <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {new Date(label).toLocaleString(userLocales, WDDDMMYYYYHHMMSS)}
      </div>

      {payload.filter(Boolean).map(p => {
        const key = p?.name ?? ''
        const keyUnit = p?.dataKey ?? ''
        const raw = p?.value
        const val =
          raw === null || raw === undefined || Number.isNaN(raw) ? '–' : raw
        const unit =
          keyUnit && unitLookup && unitLookup[keyUnit]
            ? ` ${unitLookup[keyUnit]}`
            : ''

        return (
          <div
            key={key}
            style={{ display: 'flex', gap: 6, alignItems: 'center' }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: p?.color || '#999',
              }}
            />
            <span>{key}:</span>
            <strong>
              {val}
              {unit}
            </strong>
          </div>
        )
      })}
    </div>
  )
}

type CorrelationProps = {
  isCorrelational: boolean
  PAGE_CONFIG?: string
}

export const Correlation = ({
  isCorrelational,
  PAGE_CONFIG = 'settings_correlation',
}: CorrelationProps) => {
  const { id } = useParams()
  const location = useLocation()
  const sId: SensorId = location.state?.sensorId

  const theme = useTheme()

  const fId: FolderId = Number(id)
  const fStatus: FolderStatus = useFolderStatus(fId)

  const [config, setConfig] = useState(getSession(PAGE_CONFIG))
  const { appConfig } = useAppConfig()
  const { salutationMode } = appConfig

  const [consumptionSensors, setConsumptionSensors] = useState<SensorRecord[]>(
    []
  )

  const securitySensors: SensorRecord[] = useSecuritySensors(fId)

  const [selectedSensors, setSelectedSensors] = useState([])
  const [selectedFeelingSensors, setSelectedFeelingSensors] = useState(
    sId ? [sId] : []
  )
  const [feelingSensorId, setFeelingSensorId] = useState<SensorId>(null)

  const period = 'day'

  const [dataEnergy, setDataEnergy] = useState([])

  const [tsDataList, setTsDataList] = useState({ items: [] })

  const [periodWarning, setPeriodWarning] = useState(null)
  const [sensorWarning, setSensorWarning] = useState<boolean>(false)
  const [sensorWarningText, setSensorWarningText] = useState('')
  const [sensorsWithWarning, setSensorsWithWarning] = useState([])
  const [warningAnchorEl, setWarningAnchorEl] = useState<HTMLElement | null>(
    null
  )

  // KPIs
  const [periodInDays, setPeriodInDays] = useState<number>(0)

  const [limitModal, setLimitModal] = useState(false)

  const [selectedBaselineFeelingSensors, setSelectedBaselineFeelingSensors] =
    useState([])
  const [disableBaseline, setDisableBaseline] = useState(false)

  const [hasMainEnergySensor, setHasMainEnergySensor] = useState(true)
  const [hasMainWaterSensor, setHasMainWaterSensor] = useState(true)
  const [hasMainGasSensor, setHasMainGasSensor] = useState(true)
  const [typeOfCorrection, setTypeOfCorrection] = useState(3)
  const [availableCorrectionTypes, setAvailableCorrectionTypes] = useState([])

  const [range, setRange] = useState({
    fromDate: config?.fromDate,
    toDate: config?.toDate,
    granularity: config?.granularity,
  })

  const granularityData = [
    { value: 'd', label: 'Stündlich' },
    { value: 'm', label: 'Täglich' },
  ]

  useEffect(() => setSession(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  const fData: FolderSingle = useFolder(fId)
  const fSensors: SensorRecord[] = useFolderSensors(fId)
  const sPool: SensorPool = useSensorPool()

  const { getFolderConsumption } = useConsumptionApi()

  const [consumptionData, setConsumptionData] = useState([])

  const [oldFeelingSensors, setOldFeelingSensors] = useState([])
  const [oldSecuritySensors, setOldSecuritySensors] = useState([])

  useEffect(() => {
    setOldSecuritySensors(
      fSensors
        .filter(FILTER_TARGET_NOT_PERFORMANCE)
        .filter(FILTER_TARGET_SECURITY)
        .sort(SORT_NAME)
    )

    setOldFeelingSensors(
      fSensors
        .filter(FILTER_TARGET_NOT_PERFORMANCE)
        .filter(FILTER_TARGET_NOT_SECURITY)
        .sort(SORT_NAME)
    )
  }, [fSensors])

  useEffect(() => {
    const fetchConsumption = async () => {
      const data = await getFolderConsumption(
        fId,
        config.granularity,
        config.fromDate + ' 00:00:00',
        config.toDate + ' 23:59:59'
      )

      setConsumptionData(data)
    }

    fetchConsumption()
  }, [
    getFolderConsumption,
    fId,
    config.fromDate,
    config.toDate,
    config.granularity,
  ])

  const { getByFeelingTypes, getTimeSeries } = useTimeSeriesApi()

  const [tsData, setTsData] = useState([])

  useEffect(() => {
    const fetchTimeSeries = async () => {
      const { fromDate, toDate, granularity } = config

      const data = await getTimeSeries(
        feelingSensorId,
        fromDate + ' 00:00:00',
        toDate + ' 23:59:59',
        granularity
      )

      setTsData(data)
    }

    fetchTimeSeries()
  }, [getTimeSeries, config, feelingSensorId])

  const [tsDataByFeelingTypes, setTsDataByFeelingTypes] = useState<any>({})

  useEffect(() => {
    const fetchData = async () => {
      const { fromDate, toDate } = config

      const data = await getByFeelingTypes(
        fData,
        fromDate + ' 00:00:00',
        toDate + ' 23:59:59'
      )

      setTsDataByFeelingTypes(data)
    }

    fetchData()
  }, [getByFeelingTypes, config, fData])

  useEffect(() => {
    const items = tsDataByFeelingTypes?.items || {}
    setTsDataList({ items })
  }, [tsDataByFeelingTypes])

  const colorById = useMemo(() => {
    const unifiedPalette = [
      ...colorSchemeConsumption,
      ...colorSchemeFeeling,
      ...colorSchemeSecurity,
    ]
    const map = {}
    const all = [...fSensors].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''))
    )
    all.forEach((s, idx) => {
      map[s.entity_id] = unifiedPalette[idx % unifiedPalette.length]
    })
    return map
  }, [fSensors])

  const { baselineLinesMin, baselineLinesMax } = useMemo(() => {
    const mins = new Map()
    const maxs = new Map()

    selectedBaselineFeelingSensors.forEach(s => {
      if (s.alarm_min && s.alarm_min !== NOT_SPECIFIED) {
        const key = Number(s.alarm_min)
        const prev = mins.get(key) || []
        mins.set(key, [...prev, `${s.name}-${s.unit || ''}`])
      }
      if (s.alarm_max && s.alarm_max !== NOT_SPECIFIED) {
        const key = Number(s.alarm_max)
        const prev = maxs.get(key) || []
        maxs.set(key, [...prev, `${s.name}-${s.unit || ''}`])
      }
    })

    const mk = (map, prefix) =>
      Array.from(map.entries()).map(([val, arr]) => ({
        y: val,
        label: `${prefix} (${arr.join(', ')})`,
        unit: arr[0]?.split('-')[1] || '',
      }))

    return {
      baselineLinesMin: mk(mins, 'GMin'),
      baselineLinesMax: mk(maxs, 'GMax'),
    }
  }, [selectedBaselineFeelingSensors])

  const { rechartsSeries, unitLookup, mergedData } = useMemo(() => {
    const series = []
    const units = []
    const allTs = new Set<number>()

    // Feeling & Security
    selectedFeelingSensors.filter(Boolean).forEach(id => {
      const sensor = fSensors.find(f => Number(f.entity_id) === Number(id))
      if (!sensor) return
      const name = `${sensor.name}_${id}`
      const color = colorById[id]

      const unit = sensor.unit || ''
      const pts = (tsDataList?.items?.[String(id)] || []).map(d => {
        const ts = Number(d.timestamp) * 1000
        allTs.add(ts)
        return { ts, [name]: d.value === null ? null : Number(d.value) }
      })
      if (pts.length > 0) {
        units.push(unit)
        series.push({
          id: String(id),
          name: sensor.name,
          axis: sensor.unit,
          key: `${sensor.name}_${id}`,
          color,
          unit,
          data: pts,
        })
      }
    })

    // Consumption
    selectedSensors.filter(Boolean).forEach(id => {
      const sensor = fSensors.find(f => Number(f.entity_id) === Number(id))
      if (!sensor) return
      const name = `${sensor.name}_${id}`
      const color = colorById[id]
      const unit = sensor.unit || ''
      const pts = (dataEnergy[String(id)]?.widget?.[0]?.data || []).map(
        ([t, v]) => {
          const ts = Number(t)
          allTs.add(ts)
          return { ts, [name]: v === null ? null : Number(v) }
        }
      )
      if (pts.length > 0) {
        units.push(unit)
        series.push({
          id: String(id),
          name: sensor.name,
          axis: sensor.unit,
          key: `${sensor.name}_${id}`,
          color,
          unit,
          data: pts,
        })
      }
    })

    const unitL = {}
    series.forEach(s => {
      unitL[s.key] = s.unit || ''
    })

    const sortedTs = Array.from(allTs).sort((a, b) => a - b)
    const cursors = series.map(() => 0)
    const merged = sortedTs.map(ts => {
      const row = { ts }
      series.forEach((s, si) => {
        const d = s.data
        if (!d || d.length === 0) {
          row[s.key] = null
          return
        }
        let i = cursors[si]
        while (i + 1 < d.length && d[i + 1].ts <= ts) i++
        cursors[si] = i
        let nearest = d[i]
        if (i + 1 < d.length) {
          const dl = Math.abs(ts - d[i].ts)
          const dr = Math.abs(ts - d[i + 1].ts)
          nearest = dr < dl ? d[i + 1] : d[i]
        }
        row[s.key] = nearest[s.key] ?? null
      })
      return row
    })

    return {
      rechartsSeries: series,
      unitLookup: unitL,
      mergedData: merged,
      rechartsSeriesUnits: [...new Set(units)],
    }
  }, [
    selectedFeelingSensors,
    selectedSensors,
    tsDataList,
    dataEnergy,
    fSensors,
    colorById,
  ])

  const { unitToAxis, axisLabel } = useMemo(() => {
    const perUnit: Array<{ unit: string; scale: number }> = []

    const byUnit = new Map<string, number[]>()

    rechartsSeries.forEach(s => {
      const u = s.unit || ''
      if (!byUnit.has(u)) byUnit.set(u, [])
      s.data?.forEach(pt => {
        const v = pt?.[s.key]
        if (v !== null && v !== undefined && !Number.isNaN(v))
          byUnit.get(u)!.push(Number(v))
      })
    })

    byUnit.forEach((vals, unit) => {
      if (vals.length === 0) return
      const max = Math.max(...vals)
      const scale = Math.max(1e-6, max)
      perUnit.push({ unit, scale })
    })

    if (perUnit.length === 0)
      return { unitToAxis: {}, axisLabel: { left: '', right: '' } }

    const items = perUnit
      .map(x => ({ ...x, log: Math.log10(x.scale) }))
      .sort((a, b) => a.log - b.log)

    const units = items.map(i => i.unit)

    if (units.length === 1) {
      return {
        unitToAxis: { [units[0]]: 'left' },
        axisLabel: { left: units[0], right: '' },
      }
    }
    if (units.length === 2) {
      return {
        unitToAxis: { [units[0]]: 'left', [units[1]]: 'right' },
        axisLabel: { left: units[0], right: units[1] },
      }
    }

    const sse = (arr: number[]) => {
      const m = arr.reduce((a, b) => a + b, 0) / arr.length
      return arr.reduce((a, b) => a + (b - m) * (b - m), 0)
    }

    let bestIdx = 1
    let bestScore = Number.POSITIVE_INFINITY

    for (let i = 1; i < items.length; i++) {
      const left = items.slice(0, i).map(x => x.log)
      const right = items.slice(i).map(x => x.log)
      const score = sse(left) + sse(right)
      if (score < bestScore) {
        bestScore = score
        bestIdx = i
      }
    }

    const leftUnits = items.slice(0, bestIdx).map(x => x.unit)
    const rightUnits = items.slice(bestIdx).map(x => x.unit)

    const map: Record<string, 'left' | 'right'> = {}
    leftUnits.forEach(u => {
      map[u] = 'left'
    })
    rightUnits.forEach(u => {
      map[u] = 'right'
    })

    return {
      unitToAxis: map,
      axisLabel: {
        left: leftUnits.join(' / '),
        right: rightUnits.join(' / '),
      },
    }
  }, [rechartsSeries])

  const changeFeelingSensors = entity_id => {
    let _selectedFeelingSensors = [...selectedFeelingSensors]
    let _selectedBaselineFeelingSensors = [...selectedBaselineFeelingSensors]

    if (_selectedFeelingSensors.includes(entity_id)) {
      _selectedFeelingSensors = _selectedFeelingSensors.filter(
        f => f !== entity_id
      )

      const index = _selectedBaselineFeelingSensors.findIndex(
        s => s.entity_id === entity_id
      )

      if (index !== -1) {
        _selectedBaselineFeelingSensors =
          _selectedBaselineFeelingSensors.filter(f => f.entity_id !== entity_id)
      }
    } else {
      _selectedFeelingSensors.push(entity_id)
    }

    setTsDataList(prev => {
      const newItems = { ...prev.items }
      return { ...prev, items: newItems }
    })
    setSelectedFeelingSensors(_selectedFeelingSensors)
    setSelectedBaselineFeelingSensors(_selectedBaselineFeelingSensors)
  }

  const onToggleWarning = (event: MouseEvent<HTMLElement>) =>
    setWarningAnchorEl(warningAnchorEl ? null : event.currentTarget)

  useEffect(() => {
    const { fromDate, granularity, toDate } = range

    const _1H = 60 * 60 * 1000
    const _24H = 24 * _1H

    const _periodInDays =
      (new Date(toDate).getTime() - new Date(fromDate).getTime()) / _24H

    setPeriodInDays(_periodInDays)

    switch (granularity) {
      case 'd':
        // timespan max a week
        setPeriodWarning(
          _periodInDays > 6
            ? 'Diese Granularität ist bei einer Auswahl von bis zu 7 Tagen verfügbar.'
            : selectedSensors.length > 4
              ? 'Diese Ansicht erlaubt maximal 4 Messpunkte aus dem Bereich Leistung.'
              : null
        )
        break
      case 'm':
        // timespan max. half a year
        setPeriodWarning(
          _periodInDays > 182.625
            ? 'Diese Granularität ist bei einer Auswahl von bis zu 6 Monaten verfügbar.'
            : null
        )
        break
      case 'y':
        setPeriodWarning(null)
        break
      default:
        break
    }
  }, [range, selectedSensors])

  useEffect(() => {
    if (!sPool) return

    let foundWarning = false
    const sensorsWithWarning = []

    selectedSensors.forEach((id: SensorId) => {
      const sensor = sPool.find(f => Number(f.id) === id)
      if (!sensor) return

      const { name } = sensor.attributes

      const sensorConsumption = consumptionData.find(
        f => Number(f.entity_id) === id
      )

      if (
        typeOfCorrection === 1 &&
        sensorConsumption?.heating_corrected_consumptions?.values.length === 0
      ) {
        foundWarning = true
        sensorsWithWarning.push(name)
      }
      if (
        typeOfCorrection === 2 &&
        sensorConsumption?.cooling_corrected_consumptions?.values.length === 0
      ) {
        foundWarning = true
        sensorsWithWarning.push(name)
      }
    })

    selectedFeelingSensors.forEach((id: SensorId) => {
      const sensor = sPool.find(f => Number(f.id) === id)
      if (!sensor) return
      const { name } = sensor.attributes
      if (typeOfCorrection === 1 || typeOfCorrection === 2) {
        foundWarning = true
        sensorsWithWarning.push(name)
      }
    })

    if (foundWarning) {
      setSensorWarning(true)
      setSensorWarningText(
        'Die TB steht nicht für alle ausgewählten Messpunkte zur Verfügung!'
      )
      setSensorsWithWarning(sensorsWithWarning)
    } else {
      setSensorWarning(false)
      setSensorWarningText('')
      setSensorsWithWarning([])
    }
  }, [
    typeOfCorrection,
    selectedSensors,
    consumptionData,
    sPool,
    selectedFeelingSensors,
  ])

  useEffect(() => {
    if (!sPool) return

    const checkForMainSensor = type =>
      sPool.some(
        s =>
          s.attributes.parent_id === null &&
          s.attributes.type_in_folder === type &&
          Number(s.attributes.folder_id) === fId
      )

    setHasMainEnergySensor(checkForMainSensor('energy'))
    setHasMainWaterSensor(checkForMainSensor('water'))
    setHasMainGasSensor(checkForMainSensor('gas'))
  }, [fId, sPool])

  const sIds: SensorId[] = useSensorPoolIds()

  useEffect(() => {
    if (consumptionData.length === 0 || sIds.length === 0) return

    const cSensors = consumptionData.filter(f =>
      sIds.includes(Number(f.entity_id))
    )

    const FILTER_MAIN_SENSOR = f => f.parent_id === null

    const mainSensor = consumptionData
      .filter(f => sIds.includes(Number(f.entity_id)))
      .find(
        hasMainEnergySensor || hasMainGasSensor || hasMainWaterSensor
          ? FILTER_MAIN_SENSOR
          : FILTER_SUB_SENSOR
      )

    if (!mainSensor) return

    const columnsChart = []

    cSensors.forEach(s => {
      let { consumption } = s
      const {
        cooling_corrected_consumptions,
        heating_corrected_consumptions,
        entity_id,
      } = s

      if (typeOfCorrection === 1) consumption = heating_corrected_consumptions
      if (typeOfCorrection === 2) consumption = cooling_corrected_consumptions

      if (selectedSensors.includes(entity_id) && !columnsChart[entity_id]) {
        columnsChart[entity_id] = {
          ui: { min_period: 0, period, max_period: 0 },
          widget: [{ period, type: 'bid', data: [], display: 'absolute' }],
        }

        let minPeriod = 0
        let maxPeriod = 0
        const { ui, widget } = columnsChart[entity_id]

        consumption?.values?.forEach(item => {
          const { timepoint, value } = item

          const time: number = new Date(timepoint).getTime()
          const val: number = Number(value.toFixed(2))

          if (val > maxPeriod) maxPeriod = val
          else if (val < minPeriod) minPeriod = val

          widget[0].data.push([time, val])
        })

        ui.min_period = minPeriod
        ui.max_period = maxPeriod

        if (widget[0].data.length === 0) {
          const timestamp = new Date().getTime()

          widget[0].data.push([timestamp, 0])
        }
      }
    })

    // sort sensors by its names (label)
    cSensors.sort(SORT_NAME)

    setConsumptionSensors(cSensors)
    setDataEnergy(columnsChart)
  }, [
    selectedSensors,
    consumptionData,
    hasMainEnergySensor,
    hasMainGasSensor,
    hasMainWaterSensor,
    sIds,
    period,
    typeOfCorrection,
  ])

  useEffect(() => {
    if (!tsData || !feelingSensorId) return

    setTsDataList(prev => ({
      ...prev,
      items: { ...prev.items, [feelingSensorId]: [...tsData] },
    }))
  }, [tsData, feelingSensorId])

  useEffect(
    () => setDisableBaseline(selectedBaselineFeelingSensors.length >= 6),
    [selectedBaselineFeelingSensors]
  )

  useEffect(() => {
    if (consumptionData.length === 0) return

    const hasHeating = consumptionData.some(
      s => s.heating_corrected_consumptions?.values?.length
    )

    const hasCooling = consumptionData.some(
      s => s.cooling_corrected_consumptions?.values?.length
    )

    const allowedValues = new Set([
      CORRECTION_ABSOLUT,
      ...(hasHeating ? [CORRECTION_HEATING] : []),
      ...(hasCooling ? [CORRECTION_COOLING] : []),
    ])

    const availableCorrectionTypes = correctionTypeData.filter(ct =>
      allowedValues.has(ct.value)
    )

    setAvailableCorrectionTypes(availableCorrectionTypes)
  }, [consumptionData])

  const contractData: FolderContractView = useFolderContract(fId)

  const contractBoundaries = date => {
    const date_begin = contractData?.date_begin
    const date_expire = contractData?.date_expire

    if (date < date_begin) return date_begin
    if (date > date_expire) return date_expire
    return date
  }

  const sanitize = date => limitTodayMax(contractBoundaries(date))

  const LimitSwitches = ({ sensors }) =>
    sensors.map(s => {
      const { entity_id, name, attributes_values: av, unit } = s

      const alarm_min =
        av?.find(f => f.attribute_code === 'alarm_min')?.value ?? NOT_SPECIFIED

      const alarm_max =
        av?.find(f => f.attribute_code === 'alarm_max')?.value ?? NOT_SPECIFIED

      const minObj = { entity_id, alarm_min, name, unit }
      const maxObj = { entity_id, alarm_max, name, unit }

      if (selectedFeelingSensors.includes(entity_id)) {
        let disabledMin = disableBaseline
        let disabledMax = disableBaseline

        if (
          selectedBaselineFeelingSensors.filter(
            s => s.entity_id === entity_id && s.alarm_min === alarm_min
          ).length > 0
        )
          disabledMin = false

        if (
          selectedBaselineFeelingSensors.filter(
            s => s.entity_id === entity_id && s.alarm_max === alarm_max
          ).length > 0
        )
          disabledMax = false

        const minChecked =
          selectedBaselineFeelingSensors.filter(
            s => s.entity_id === entity_id && s.alarm_min === alarm_min
          ).length > 0

        const maxChecked =
          selectedBaselineFeelingSensors.filter(
            s => s.entity_id === entity_id && s.alarm_max === alarm_max
          ).length > 0

        const onMinChange = () => {
          if (
            selectedBaselineFeelingSensors.filter(
              s => s.entity_id === entity_id && s.alarm_min === alarm_min
            ).length > 0
          ) {
            const newData = removeBaselineSensor(
              selectedBaselineFeelingSensors,
              entity_id,
              'alarm_min',
              alarm_min
            )
            setSelectedBaselineFeelingSensors(newData)
          } else {
            setSelectedBaselineFeelingSensors([
              ...selectedBaselineFeelingSensors,
              minObj,
            ])
          }
        }

        const onMaxChange = () => {
          if (
            selectedBaselineFeelingSensors.filter(
              s => s.entity_id === entity_id && s.alarm_max === alarm_max
            ).length > 0
          ) {
            const newData = removeBaselineSensor(
              selectedBaselineFeelingSensors,
              entity_id,
              'alarm_max',
              alarm_max
            )
            setSelectedBaselineFeelingSensors(newData)
          } else {
            setSelectedBaselineFeelingSensors([
              ...selectedBaselineFeelingSensors,
              maxObj,
            ])
          }
        }

        return (
          <HStack spacing={2} key={entity_id} sx={{ px: 1, width: 1 }}>
            <List sx={{ width: 1 }}>
              <ListItem sx={{ p: 0 }}>
                <ListItemText primary={name} sx={{ width: 0.33 }} />

                <FormControlLabel
                  control={
                    <Switch checked={minChecked} onChange={onMinChange} />
                  }
                  disabled={disabledMin}
                  label={`${alarm_min} ${unit}`}
                  labelPlacement={'start'}
                  sx={{ width: 0.33 }}
                />

                <FormControlLabel
                  control={
                    <Switch checked={maxChecked} onChange={onMaxChange} />
                  }
                  disabled={disabledMax}
                  label={`${alarm_max} ${unit}`}
                  labelPlacement={'start'}
                  sx={{ width: 0.33 }}
                />
              </ListItem>
            </List>
          </HStack>
        )
      }

      return null
    })

  const onChangeGranularity = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRange = { ...range, granularity: event.target.value }
    setRange(newRange)
  }

  const onChangeTypeOfCorrection = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = Number(event.target.value)
    setTypeOfCorrection(value)
  }

  const justifyContent = 'space-between'

  return (
    <Collapse in={true}>
      {/* limits */}
      <Dialog
        maxWidth={'xl'}
        open={limitModal}
        onClose={() => setLimitModal(false)}
      >
        <DialogTitle>Grenzwerte der Messpunkte</DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Stack>
            <Alert severity={'info'}>
              Hier {salutationMode === 'default' ? 'können Sie' : 'kannst Du'}{' '}
              auswählen, ob für die ausgewählten Messpunkte Grenzwertlinien
              eingeblendet werden sollen. (max. 6)
            </Alert>

            <HStack
              justifyContent={justifyContent}
              spacing={2}
              sx={{ px: 2, py: 1, width: 1 }}
            >
              <Typography sx={{ py: 1, textAlign: 'center' }}>
                Messpunkt
              </Typography>

              <Typography sx={{ py: 1, textAlign: 'center' }}>
                Minimum
              </Typography>

              <Typography sx={{ py: 1, textAlign: 'center' }}>
                Maximum
              </Typography>
            </HStack>

            <LimitSwitches sensors={oldFeelingSensors} />
            <LimitSwitches sensors={oldSecuritySensors} />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setLimitModal(false)}>Schliessen</Button>
        </DialogActions>
      </Dialog>
      {/* limits end */}

      {isCorrelational === true ? (
        <Collapse in={true}>
          <Card
            variant={'correlation'}
            sx={{
              minHeight: 'auto',
              border:
                typeOfCorrection !== 3
                  ? `1px solid ${theme.palette.primary.main}`
                  : '1px solid #ccc',
            }}
          >
            <CardHeader
              title={
                <HStack sx={{ display: 'flex', justifyContent }}>
                  <HStack alignItems={'center'}>
                    <TextField
                      label={'Granularität'}
                      onChange={onChangeGranularity}
                      select
                      type={'text'}
                      value={range.granularity}
                      variant={'standard'}
                    >
                      {granularityData.map(item => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Tooltip title={'Grenzwerte'}>
                      <ToggleButton
                        onClick={() => setLimitModal(true)}
                        value={limitModal}
                      >
                        <NotificationImportantTwoTone />
                      </ToggleButton>
                    </Tooltip>
                    <FormControlLabel
                      control={
                        <TextField
                          label={'Bereinigungsverfahren'}
                          disabled={
                            range.granularity === 'd' ||
                            consumptionSensors.length === 0
                          }
                          onChange={onChangeTypeOfCorrection}
                          select
                          type={'text'}
                          value={typeOfCorrection}
                          variant={'standard'}
                          sx={{ minWidth: 140 }}
                          SelectProps={{
                            MenuProps: {
                              PaperProps: { style: { minWidth: 140 } },
                            },
                          }}
                        >
                          {availableCorrectionTypes.map(item => (
                            <MenuItem key={item.label} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      }
                      label={''}
                    />
                    <Tooltip
                      title={
                        'Durch die Temperaturbereinigung werden witterungsbedingte Einflüsse ausgeglichen, sodass energiebezogene Kennzahlen standort- und zeitunabhängig vergleichbar dargestellt werden können (Berechnung erfolgt in Anlehnung an VDI 3807 Blatt 1).'
                      }
                    >
                      <IconButton>
                        <InfoTwoTone />
                      </IconButton>
                    </Tooltip>
                  </HStack>

                  <HStack alignItems={'center'}>
                    <Tooltip
                      title={`Um ${Math.max(1, Math.ceil((periodInDays ?? 0) + 1))} Tage zurück blättern`}
                    >
                      <span>
                        <IconButton
                          disabled={
                            periodWarning ||
                            contractData?.date_begin >= range.fromDate
                          }
                          onClick={() => {
                            const step = Math.max(
                              1,
                              Math.ceil((periodInDays ?? 0) + 1)
                            )
                            const newConfig = {
                              ...config,
                              fromDate: daysAgo(config.fromDate, step),
                              toDate: daysAgo(config.toDate, step),
                            }

                            setConfig(newConfig)
                            setRange({
                              ...range,
                              fromDate: newConfig.fromDate,
                              toDate: newConfig.toDate,
                            })
                          }}
                        >
                          <ArrowBackIosTwoTone />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={`Um ${Math.max(1, Math.ceil((periodInDays ?? 0) + 1))} Tage weiter blättern`}
                    >
                      <span>
                        <IconButton
                          disabled={
                            periodWarning ||
                            contractData?.date_expire <= range.toDate ||
                            new Date(Date.now()).toISOString().split('T')[0] <=
                              config.toDate
                          }
                          onClick={() => {
                            const step = Math.max(
                              1,
                              Math.ceil((periodInDays ?? 0) + 1)
                            )
                            const newConfig = {
                              ...config,
                              fromDate: daysOnward(config.fromDate, step),
                              toDate: daysOnward(config.toDate, step),
                            }
                            setConfig(newConfig)
                            setRange({
                              ...range,
                              fromDate: newConfig.fromDate,
                              toDate: newConfig.toDate,
                            })
                          }}
                        >
                          <ArrowForwardIosTwoTone />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <TextField
                      inputProps={{
                        onChange: event => {
                          const value = (event.target as HTMLInputElement).value

                          const newRange = {
                            ...range,
                            fromDate: sanitize(value),
                            ...(sanitize(value) > range.toDate && {
                              toDate: sanitize(value),
                            }),
                          }
                          setRange(newRange)
                        },
                        min: contractData?.date_begin,
                        max: contractData?.date_expire,
                      }}
                      label={'von'}
                      sx={{ width: 150 }}
                      type={'date'}
                      value={range.fromDate}
                      variant={'standard'}
                    />
                    <TextField
                      inputProps={{
                        onChange: event => {
                          const value = (event.target as HTMLInputElement).value

                          const newRange = {
                            ...range,
                            ...(sanitize(value) < range.fromDate && {
                              fromDate: sanitize(value),
                            }),
                            toDate: sanitize(value),
                          }

                          setRange(newRange)
                        },
                        min: contractData?.date_begin,
                        max: contractData?.date_expire,
                      }}
                      label={'bis'}
                      sx={{ width: 150 }}
                      type={'date'}
                      value={range.toDate}
                      variant={'standard'}
                    />
                    {/* aktuell */}

                    <Tooltip
                      title={`Zeitspanne ändern: ${
                        Number(
                          new Date(config.toDate).getTime() -
                            new Date(config.fromDate).getTime()
                        ) /
                        1000 /
                        60 /
                        60 /
                        24
                      } > ${
                        Number(
                          new Date(range.toDate).getTime() -
                            new Date(range.fromDate).getTime()
                        ) /
                        1000 /
                        60 /
                        60 /
                        24
                      } Tag(e)`}
                    >
                      <span>
                        <Button
                          color={'primary'}
                          disabled={
                            config.fromDate === range.fromDate &&
                            config.toDate === range.toDate &&
                            config.granularity === range.granularity
                          }
                          onClick={() => {
                            setConfig({
                              ...config,
                              fromDate: range.fromDate,
                              toDate: range.toDate,
                              granularity: range.granularity,
                            })
                          }}
                        >
                          Anwenden
                        </Button>
                      </span>
                    </Tooltip>
                  </HStack>
                </HStack>
              }
              variant={'correlation'}
            />

            <CardContent sx={{ p: '0px !important' }}>
              {periodWarning && (
                <Collapse in={true}>
                  <Alert severity={'warning'}>
                    <AlertTitle>Achtung!</AlertTitle>
                    {periodWarning}
                  </Alert>
                </Collapse>
              )}

              <Collapse in={!periodWarning}>
                {sensorWarning && (
                  <Collapse in={true} sx={{ position: 'relative' }}>
                    <Alert severity={'warning'} sx={{ mb: 5 }}>
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={justifyContent}
                        width={'100%'}
                      >
                        <Typography>{sensorWarningText}</Typography>
                        <Tooltip title={'Messpunkte in Übersicht'}>
                          <IconButton
                            onClick={onToggleWarning}
                            size={'small'}
                            sx={{ pl: 1, pt: 0, pr: 0, pb: 0 }}
                          >
                            <InfoTwoTone />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Alert>

                    <Popover
                      anchorEl={warningAnchorEl}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      onClose={() => setWarningAnchorEl(null)}
                      open={Boolean(warningAnchorEl)}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 700, px: 2, py: 1 }}>
                          Messpunkte in Übersicht
                        </Typography>

                        <Divider />

                        <List sx={{ p: 1 }}>
                          {sensorsWithWarning.map(s => (
                            <ListItem key={s}>
                              <ListItemText
                                primary={s}
                                sx={{ fontWeight: 10 }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Popover>
                  </Collapse>
                )}

                {/* Recharts Chart */}
                <div style={{ height: 500, marginTop: 40 }}>
                  <ResponsiveContainer width={'100%'} height={'100%'}>
                    {mergedData.length > 0 ? (
                      <LineChart
                        data={mergedData}
                        margin={{
                          top: 5,
                          right: 20,
                          left: 10,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray={'3 3'} />
                        <XAxis
                          dataKey={'ts'}
                          type={'number'}
                          domain={['dataMin', 'dataMax']}
                          tickFormatter={t =>
                            new Date(t).toLocaleString(userLocales, WDDDMMYY)
                          }
                        />
                        <YAxis
                          yAxisId={'left'}
                          orientation={'left'}
                          domain={[0, 'auto']}
                          tickFormatter={v =>
                            Number(v).toLocaleString(userLocales)
                          }
                          label={{
                            value: axisLabel.left,
                            angle: -90,
                            position: 'insideLeft',
                            style: { textAnchor: 'middle' },
                          }}
                        />

                        <YAxis
                          yAxisId={'right'}
                          orientation={'right'}
                          domain={[0, 'auto']}
                          tickFormatter={v =>
                            Number(v).toLocaleString(userLocales)
                          }
                          label={{
                            value: axisLabel.right,
                            angle: -90,
                            position: 'insideRight',
                            style: { textAnchor: 'middle' },
                          }}
                        />

                        <Recharts_Tooltip
                          content={
                            <CustomTooltip
                              userLocales={userLocales}
                              unitLookup={unitLookup}
                            />
                          }
                        />

                        <Legend />
                        {baselineLinesMin.map(b => (
                          <ReferenceLine
                            key={`min-${b.y}-${b.label}`}
                            y={b.y}
                            yAxisId={unitToAxis[b.unit || ''] ?? 'left'}
                            stroke={'#ef4444'}
                            strokeDasharray={'4 4'}
                            ifOverflow={'extendDomain'}
                            label={{
                              value: b.label,
                              position: 'insideRight',
                              fill: '#ef4444',
                              dx: -4,
                              dy: 10,
                            }}
                          />
                        ))}
                        {baselineLinesMax.map(b => (
                          <ReferenceLine
                            key={`max-${b.y}-${b.label}`}
                            y={b.y}
                            yAxisId={unitToAxis[b.unit || ''] ?? 'left'}
                            stroke={'#10b981'}
                            strokeDasharray={'4 4'}
                            ifOverflow={'extendDomain'}
                            label={{
                              value: b.label,
                              position: 'insideRight',
                              fill: '#10b981',
                              dx: -4,
                              dy: 10,
                            }}
                          />
                        ))}
                        {rechartsSeries
                          .filter(s => s.data && s.data.length > 0)
                          .map(s => (
                            <Line
                              key={s.id}
                              type={'monotone'}
                              name={s.name}
                              dataKey={`${s.name}_${s.id}`}
                              stroke={s.color}
                              yAxisId={unitToAxis[s.unit || ''] ?? 'left'}
                              dot={false}
                              isAnimationActive={false}
                              connectNulls
                            />
                          ))}
                        <Brush
                          dataKey={'ts'}
                          height={20}
                          tickFormatter={t =>
                            new Date(t).toLocaleString(userLocales, WDDDMMYY)
                          }
                        />
                      </LineChart>
                    ) : (
                      <Box />
                    )}
                  </ResponsiveContainer>
                </div>
              </Collapse>
            </CardContent>
          </Card>

          <HStack alignItems={'top'} spacing={2}>
            <Card variant={'correlation'} sx={{ width: 1 }}>
              <CardHeader
                avatar={
                  <Avatar>
                    <SVGStatus
                      type={'performance'}
                      status={fStatus.performance}
                    />
                  </Avatar>
                }
                title={'Leistung'}
                titleTypographyProps={{ variant: 'h6' }}
                variant={'correlation'}
              />

              <CardContent sx={{ p: '0 !important' }}>
                <AccConsumption
                  data={{
                    selected: selectedSensors,
                    sensors: consumptionSensors,
                  }}
                  id={fId}
                  onChange={setSelectedSensors}
                />
              </CardContent>
            </Card>

            <Card variant={'correlation'} sx={{ width: 1 }}>
              <CardHeader
                avatar={
                  <Avatar>
                    <SVGStatus type={'feeling'} status={fStatus.feeling} />
                  </Avatar>
                }
                title={'Wohlbefinden'}
                titleTypographyProps={{ variant: 'h6' }}
                variant={'correlation'}
              />

              <CardContent sx={{ p: '0 !important' }}>
                <AccFeeling
                  data={{
                    baseline: selectedBaselineFeelingSensors,
                    selected: selectedFeelingSensors,
                  }}
                  id={fId}
                  onChange={setSelectedFeelingSensors}
                  onChangeBaseline={setSelectedBaselineFeelingSensors}
                  setFeelingSensorId={setFeelingSensorId}
                  changeFeelingSensors={changeFeelingSensors}
                />
              </CardContent>
            </Card>

            <Card variant={'correlation'} sx={{ width: 1 }}>
              <CardHeader
                avatar={
                  <Avatar>
                    <SVGStatus type={'security'} status={fStatus.security} />
                  </Avatar>
                }
                title={'Sicherheit'}
                titleTypographyProps={{ variant: 'h6' }}
                variant={'correlation'}
              />

              {['temperature', 'co2', 'humidity', 'light', 'pressure'].map(
                medium =>
                  securitySensors.find(s => nst(s.sensor_type) === medium) ? (
                    <AccSecurity
                      data={{
                        baseline: selectedBaselineFeelingSensors,
                        medium: medium,
                        selected: selectedFeelingSensors,
                      }}
                      id={fId}
                      key={medium}
                      onChange={setSelectedFeelingSensors}
                      onChangeBaseline={setSelectedBaselineFeelingSensors}
                      setFeelingSensorId={setFeelingSensorId}
                      changeFeelingSensors={changeFeelingSensors}
                    />
                  ) : null
              )}
            </Card>
          </HStack>
        </Collapse>
      ) : (
        <Alert severity={'info'}>
          Diese Funktion ist verfügbar, sobald mindestens zwei Messpunkte
          gebucht sind.
        </Alert>
      )}
    </Collapse>
  )
}
